import { useState, useEffect, useCallback, useRef } from 'react';
import { DeadlineRule, DeadlineRuleType, DayOfWeek, CreateDeadlineRuleInput } from '@/types';
import {
  AllWeekdayRules,
  createDefaultAllWeekdayRules,
  DeadlineRuleModifications,
} from '@/types/deadlineRule';
import {
  normalizeTime,
  createRuleMap,
  getRuleMapKey,
  createDefaultRuleData,
  getDayOfWeekShortLabel,
  devLog,
} from '@/utils/deadlineRuleUtils';

interface UseDeadlineRulesProps {
  rules: DeadlineRule[];
  businessPartnerId: string;
  companyId: string;
}

export const useDeadlineRules = ({
  rules,
  businessPartnerId,
  companyId,
}: UseDeadlineRulesProps) => {
  // 全曜日の締切設定（引取・搬入統合、月曜日始まり）
  const [weekdayRules, setWeekdayRules] = useState<AllWeekdayRules>(createDefaultAllWeekdayRules());

  // 即時参照用のref（同期的にgetModifiedRulesの結果に反映させるため）
  const weekdayRulesRef = useRef<AllWeekdayRules>(createDefaultAllWeekdayRules());

  // 元のルールを保持（キャンセル時の復元用）
  const [originalRules, setOriginalRules] = useState<DeadlineRule[]>([]);

  // 曜日ごとの有効状態（セクションの表示/非表示を制御）
  const [enabledDays, setEnabledDays] = useState<Record<number, boolean>>({
    [DayOfWeek.MONDAY]: false,
    [DayOfWeek.TUESDAY]: false,
    [DayOfWeek.WEDNESDAY]: false,
    [DayOfWeek.THURSDAY]: false,
    [DayOfWeek.FRIDAY]: false,
    [DayOfWeek.SATURDAY]: false,
    [DayOfWeek.SUNDAY]: false,
  });

  // 状態更新のヘルパー関数
  const setWeekdayRulesState = useCallback(
    (updater: AllWeekdayRules | ((prev: AllWeekdayRules) => AllWeekdayRules)) => {
      if (typeof updater === 'function') {
        setWeekdayRules(prev => {
          const next = updater(prev);
          weekdayRulesRef.current = next;
          return next;
        });
      } else {
        weekdayRulesRef.current = updater;
        setWeekdayRules(updater);
      }
    },
    []
  );

  // 既存ルールを曜日・種別でグループ化（月曜日始まり）
  useEffect(() => {
    devLog('useDeadlineRules: ルール更新開始', {
      rulesCount: rules.length,
      businessPartnerId,
    });

    // ビジネスパートナーIDが変更された場合のみ、完全リセットを実行
    const isBusinessPartnerChanged =
      originalRules.length > 0 && businessPartnerId !== originalRules[0]?.businessPartnerId;

    if (rules.length === 0 && originalRules.length > 0 && !isBusinessPartnerChanged) {
      // 同じビジネスパートナーで、締切ルールが空の場合はユーザー操作を維持
      devLog('同じパートナー・ルール空、現在の状態を維持');
      setOriginalRules([]); // originalRulesだけは更新
      return;
    }

    const newWeekdayRules = createDefaultAllWeekdayRules();

    rules.forEach(rule => {
      const ruleData = {
        id: rule.id,
        deadlineTime: normalizeTime(rule.deadlineTime),
        isActive: rule.isActive,
      };

      if (rule.ruleType === DeadlineRuleType.PICKUP) {
        newWeekdayRules[rule.dayOfWeek].pickup = ruleData;
      } else {
        newWeekdayRules[rule.dayOfWeek].delivery = ruleData;
      }
    });

    devLog('ルール更新完了', {
      rules: rules.length,
      newWeekdayRules: JSON.stringify(newWeekdayRules),
    });

    setWeekdayRulesState(newWeekdayRules);

    // 既存ルールがある曜日はセクションを有効化
    const nextEnabledDays: Record<number, boolean> = {};
    Object.keys(newWeekdayRules).forEach(dayOfWeekStr => {
      const dayOfWeek = parseInt(dayOfWeekStr) as DayOfWeek;
      const dayRules = newWeekdayRules[dayOfWeek];
      nextEnabledDays[dayOfWeek] = Boolean(dayRules.pickup || dayRules.delivery);
    });

    setEnabledDays(nextEnabledDays);
    setOriginalRules([...rules]);
  }, [rules, businessPartnerId, setWeekdayRulesState]);

  // 変更されたルールを取得する関数
  const getModifiedRules = useCallback((): DeadlineRuleModifications => {
    const toCreate: CreateDeadlineRuleInput[] = [];
    const toUpdate: Array<{ id: string; input: Partial<CreateDeadlineRuleInput> }> = [];
    const toDelete: DeadlineRule[] = [];

    devLog('getModifiedRules開始', {
      businessPartnerId,
      originalRulesCount: originalRules.length,
    });

    // 元のルールをマッピング
    const originalRuleMap = createRuleMap(originalRules);

    // 各曜日・種別をチェック
    const rulesSource = weekdayRulesRef.current || weekdayRules;
    Object.entries(rulesSource).forEach(([dayOfWeekStr, dayRules]) => {
      const dayOfWeek = parseInt(dayOfWeekStr) as DayOfWeek;
      const dayLabel = getDayOfWeekShortLabel(dayOfWeek);

      devLog(`${dayLabel}曜日のルールチェック`, {
        dayOfWeek,
        pickup: dayRules.pickup,
        delivery: dayRules.delivery,
      });

      // 引取ルールをチェック
      const pickupKey = getRuleMapKey(dayOfWeek, DeadlineRuleType.PICKUP);
      const originalPickup = originalRuleMap.get(pickupKey);
      const currentPickup = dayRules.pickup;

      if (originalPickup && (!currentPickup || currentPickup.isDeleted)) {
        devLog(`${dayLabel}曜日引取ルールを削除対象に追加`);
        toDelete.push(originalPickup);
      } else if (!originalPickup && currentPickup && !currentPickup.isDeleted) {
        devLog(`${dayLabel}曜日引取ルールを新規作成対象に追加`);
        toCreate.push({
          businessPartnerId,
          companyId,
          ruleType: DeadlineRuleType.PICKUP,
          dayOfWeek,
          deadlineTime: currentPickup.deadlineTime,
          isActive: currentPickup.isActive,
          notes: '',
        });
      } else if (originalPickup && currentPickup && !currentPickup.isDeleted) {
        const normalizedOriginalTime = normalizeTime(originalPickup.deadlineTime);
        const needsUpdate =
          normalizedOriginalTime !== currentPickup.deadlineTime ||
          originalPickup.isActive !== currentPickup.isActive;

        if (needsUpdate) {
          toUpdate.push({
            id: originalPickup.id,
            input: {
              deadlineTime: currentPickup.deadlineTime,
              isActive: currentPickup.isActive,
              notes: '',
            },
          });
        }
      }

      // 搬入ルールをチェック
      const deliveryKey = getRuleMapKey(dayOfWeek, DeadlineRuleType.DELIVERY);
      const originalDelivery = originalRuleMap.get(deliveryKey);
      const currentDelivery = dayRules.delivery;

      if (originalDelivery && (!currentDelivery || currentDelivery.isDeleted)) {
        devLog(`${dayLabel}曜日搬入ルールを削除対象に追加`);
        toDelete.push(originalDelivery);
      } else if (!originalDelivery && currentDelivery && !currentDelivery.isDeleted) {
        devLog(`${dayLabel}曜日搬入ルールを新規作成対象に追加`);
        toCreate.push({
          businessPartnerId,
          companyId,
          ruleType: DeadlineRuleType.DELIVERY,
          dayOfWeek,
          deadlineTime: currentDelivery.deadlineTime,
          isActive: currentDelivery.isActive,
          notes: '',
        });
      } else if (originalDelivery && currentDelivery && !currentDelivery.isDeleted) {
        const normalizedOriginalTime = normalizeTime(originalDelivery.deadlineTime);
        const needsUpdate =
          normalizedOriginalTime !== currentDelivery.deadlineTime ||
          originalDelivery.isActive !== currentDelivery.isActive;

        if (needsUpdate) {
          toUpdate.push({
            id: originalDelivery.id,
            input: {
              deadlineTime: currentDelivery.deadlineTime,
              isActive: currentDelivery.isActive,
              notes: '',
            },
          });
        }
      }
    });

    const result = { toCreate, toUpdate, toDelete };
    devLog('getModifiedRules結果', {
      toCreateCount: toCreate.length,
      toUpdateCount: toUpdate.length,
      toDeleteCount: toDelete.length,
    });

    return result;
  }, [weekdayRules, originalRules, businessPartnerId, companyId]);

  // 変更をキャンセルして元の状態に戻す
  const cancelChanges = useCallback(() => {
    const newWeekdayRules = createDefaultAllWeekdayRules();

    originalRules.forEach(rule => {
      const ruleData = {
        id: rule.id,
        deadlineTime: normalizeTime(rule.deadlineTime),
        isActive: rule.isActive,
      };

      if (rule.ruleType === DeadlineRuleType.PICKUP) {
        newWeekdayRules[rule.dayOfWeek].pickup = ruleData;
      } else {
        newWeekdayRules[rule.dayOfWeek].delivery = ruleData;
      }
    });

    setWeekdayRulesState(newWeekdayRules);

    // enabledDaysも元の状態に戻す
    const resetEnabledDays: Record<number, boolean> = {};
    Object.keys(newWeekdayRules).forEach(dayOfWeekStr => {
      const dayOfWeek = parseInt(dayOfWeekStr) as DayOfWeek;
      const dayRules = newWeekdayRules[dayOfWeek];
      resetEnabledDays[dayOfWeek] = Boolean(dayRules.pickup || dayRules.delivery);
    });
    setEnabledDays(resetEnabledDays);
  }, [originalRules, setWeekdayRulesState]);

  // 曜日の有効/無効切り替え（ローカルstateのみ更新）
  const handleWeekdayToggle = useCallback(
    (dayOfWeek: DayOfWeek, enabled: boolean) => {
      if (enabled) {
        // セクションを有効化（既存削除フラグがあればそのまま維持して個別チェックで復元可能に）
        setEnabledDays(prev => ({ ...prev, [dayOfWeek]: true }));
        setWeekdayRulesState(prev => {
          const current = prev[dayOfWeek];
          // 既存にID付き（元ルールあり）が一つもない場合は新規デフォルトを作成
          const hasExistingIds = Boolean(current.pickup?.id || current.delivery?.id);
          if (hasExistingIds) {
            return prev; // 個別チェックボックスで復元させる
          }
          return {
            ...prev,
            [dayOfWeek]: {
              pickup: createDefaultRuleData(DeadlineRuleType.PICKUP),
              delivery: createDefaultRuleData(DeadlineRuleType.DELIVERY),
            },
          };
        });
      } else {
        // 無効化: その日の全てのルールを削除対象にマークし、セクションを非表示
        setEnabledDays(prev => ({ ...prev, [dayOfWeek]: false }));
        setWeekdayRulesState(prev => {
          const current = prev[dayOfWeek];
          if (!current) return prev;

          return {
            ...prev,
            [dayOfWeek]: {
              pickup: current.pickup?.id
                ? {
                    id: current.pickup.id,
                    deadlineTime: current.pickup.deadlineTime,
                    isActive: false,
                    isDeleted: true,
                  }
                : null,
              delivery: current.delivery?.id
                ? {
                    id: current.delivery.id,
                    deadlineTime: current.delivery.deadlineTime,
                    isActive: false,
                    isDeleted: true,
                  }
                : null,
            },
          };
        });
      }
    },
    [setWeekdayRulesState]
  );

  // 引取締切の個別有効/無効切り替え
  const handlePickupToggle = useCallback(
    (dayOfWeek: DayOfWeek, enabled: boolean) => {
      setWeekdayRulesState(prev => {
        const current = prev[dayOfWeek];
        if (!current) return prev;

        if (enabled) {
          // 有効化：既存ルールを復元またはデフォルト値で新規作成
          const existingPickup = originalRules.find(
            r => r.dayOfWeek === dayOfWeek && r.ruleType === DeadlineRuleType.PICKUP
          );
          return {
            ...prev,
            [dayOfWeek]: {
              ...current,
              pickup: existingPickup
                ? {
                    id: existingPickup.id,
                    deadlineTime: normalizeTime(existingPickup.deadlineTime),
                    isActive: true,
                  }
                : createDefaultRuleData(DeadlineRuleType.PICKUP),
            },
          };
        } else {
          // 無効化：削除マークとisActive: falseを設定
          return {
            ...prev,
            [dayOfWeek]: {
              ...current,
              pickup: current.pickup?.id
                ? { ...current.pickup, isDeleted: true, isActive: false }
                : null,
            },
          };
        }
      });
    },
    [originalRules, setWeekdayRulesState]
  );

  // 搬入締切の個別有効/無効切り替え
  const handleDeliveryToggle = useCallback(
    (dayOfWeek: DayOfWeek, enabled: boolean) => {
      const dayLabel = getDayOfWeekShortLabel(dayOfWeek);
      devLog(`${dayLabel}曜日搬入締切トグル`, { dayOfWeek, enabled });

      setWeekdayRulesState(prev => {
        const current = prev[dayOfWeek];
        if (!current) {
          devLog(`${dayLabel}曜日の現在状態が見つかりません`, { dayOfWeek });
          return prev;
        }

        if (enabled) {
          // 有効化：既存ルールを復元またはデフォルト値で新規作成
          const existingDelivery = originalRules.find(
            r => r.dayOfWeek === dayOfWeek && r.ruleType === DeadlineRuleType.DELIVERY
          );

          devLog(`${dayLabel}曜日搬入締切を有効化`, {
            dayOfWeek,
            hasExistingRule: Boolean(existingDelivery),
          });

          return {
            ...prev,
            [dayOfWeek]: {
              ...current,
              delivery: existingDelivery
                ? {
                    id: existingDelivery.id,
                    deadlineTime: normalizeTime(existingDelivery.deadlineTime),
                    isActive: true,
                  }
                : createDefaultRuleData(DeadlineRuleType.DELIVERY),
            },
          };
        } else {
          // 無効化：削除マークとisActive: falseを設定
          devLog(`${dayLabel}曜日搬入締切を無効化`, { dayOfWeek });

          return {
            ...prev,
            [dayOfWeek]: {
              ...current,
              delivery: current.delivery?.id
                ? { ...current.delivery, isDeleted: true, isActive: false }
                : null,
            },
          };
        }
      });
    },
    [originalRules, setWeekdayRulesState]
  );

  // 曜日ルールの更新
  const updateWeekdayRule = useCallback(
    (
      ruleType: DeadlineRuleType,
      dayOfWeek: DayOfWeek,
      field: 'deadlineTime' | 'isActive',
      value: any
    ) => {
      setWeekdayRulesState(prev => {
        const current = prev[dayOfWeek];
        if (!current) return prev;

        const ruleKey = ruleType === DeadlineRuleType.PICKUP ? 'pickup' : 'delivery';
        const currentRule = current[ruleKey];
        if (!currentRule) return prev;

        return {
          ...prev,
          [dayOfWeek]: {
            ...current,
            [ruleKey]: { ...currentRule, [field]: value },
          },
        };
      });
    },
    [setWeekdayRulesState]
  );

  return {
    weekdayRules,
    enabledDays,
    getModifiedRules,
    cancelChanges,
    handleWeekdayToggle,
    handlePickupToggle,
    handleDeliveryToggle,
    updateWeekdayRule,
  };
};
